import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import RecoveryPrograms from './RecoveryPrograms';

const RECOVERY_CATEGORIES = ['Breathwork', 'Cold Exposure', 'Sauna', 'Walking'];

export default function AdminRecovery() {
  return (
    <div className="space-y-6" data-testid="admin-recovery">
      <div>
        <div className="label-mini text-[#A78BFA]">Library & Builder</div>
        <h1 className="font-display font-black text-4xl mt-1">Recovery</h1>
      </div>
      <Tabs defaultValue="library">
        <TabsList className="bg-regen-card border border-[var(--c-hair)]">
          <TabsTrigger value="library" data-testid="rec-tab-library">Activity Library</TabsTrigger>
          <TabsTrigger value="programs" data-testid="rec-tab-programs">Programs</TabsTrigger>
        </TabsList>
        <TabsContent value="library" className="mt-4"><RecoveryLibrary /></TabsContent>
        <TabsContent value="programs" className="mt-4"><RecoveryPrograms /></TabsContent>
      </Tabs>
    </div>
  );
}

function RecoveryLibrary() {
  const [acts, setActs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'Breathwork', duration_min: 10, description: '', image_url: '' });

  const load = async () => { const r = await api.get('/recovery/activities'); setActs(r.data); };
  useEffect(() => { load(); }, []);

  const openModal = (a) => {
    if (a) { setEditing(a.id); setForm(a); } else { setEditing(null); setForm({ name: '', category: 'Breathwork', duration_min: 10, description: '', image_url: '' }); }
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) await api.put(`/recovery/activities/${editing}`, form); else await api.post('/recovery/activities', form);
      toast.success('Saved'); setOpen(false); load();
    } catch { toast.error('Failed'); }
  };
  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/recovery/activities/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => openModal(null)} data-testid="add-recovery-btn" className="bg-[#7DD3FC] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> Add Activity</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Recovery Activity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="rec-name" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="bg-transparent border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{RECOVERY_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Metric</Label>
                  <Select value={form.metric_type} onValueChange={v => setForm({...form, metric_type: v})}>
                    <SelectTrigger data-testid="rec-metric" className="bg-transparent border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="steps">Steps</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{form.metric_type === 'steps' ? 'Target Steps' : 'Duration (min)'}</Label><Input type="number" value={form.target_value} onChange={e => setForm({...form, target_value: e.target.value})} data-testid="rec-target" className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
              </div>
              <div><Label>Image URL (optional)</Label><Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              {form.image_url && <img src={form.image_url} alt="preview" className="w-full max-h-40 object-cover rounded-lg" />}
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Coach Notes</Label><Textarea value={form.coach_notes} onChange={e => setForm({...form, coach_notes: e.target.value})} placeholder="e.g. 3 min at 8-12°C" className="bg-transparent border-[var(--c-hair)]" /></div>
              <Button onClick={save} data-testid="rec-save" className="w-full bg-[#7DD3FC] text-[#081726] font-semibold">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {acts.map(a => (
          <div key={a.id} className="regen-card-elevated overflow-hidden group" data-testid={`recovery-${a.id}`}>
            {a.image_url ? <img src={a.image_url} className="w-full h-32 object-cover" alt={a.name} /> : <div className="w-full h-32 bg-[var(--c-film)]" />}
            <div className="p-4">
              <div className="label-mini text-[#7DD3FC]">{a.category}</div>
              <div className="font-display font-bold mt-1">{a.name}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">{a.metric_type === 'steps' ? `${(a.target_value || 0).toLocaleString()} steps` : `${a.target_value || a.duration_min} min`}</div>
              <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition">
                <Button size="sm" variant="outline" onClick={() => openModal(a)} className="border-[var(--c-hair)] h-8"><Edit2 className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(a.id)} className="border-red-500/30 text-red-400 h-8"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
